import React, { useEffect, useState } from 'react'
import { useFormik } from 'formik'
import Modal from '@mui/material/Modal'
import { toast } from 'react-toastify'

import { usePreparation } from '@/hooks/usePreparation'
import { preparationService } from '@/services/Preparation/preparationService'

import CustomButton from '@/components/CustomButton'
import CustomGraphy from '@/components/CustomGraphy'
import CustomView from '@/components/CustomView'
import FormSelect from '@/components/FormSelect'
import Loader from '@/components/Loader'
import InputCurrency from '@/components/InputCurrency'

import { newProductDataSchema } from '@/utils/yup/schemas/preparation/new/PreparationNewSchema'
import { INews } from '@/types/customTable.types'
import { getParamByParam } from '@/utils/index'
import { getIdByName } from '@/pages/Parametrization/utils'
import { CONSTANTS } from '@/utils/CONSTANTS'

const styleModal = {
  position: 'absolute' as 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '200px',
  borderRadius: '8px',
  border: '1.5px solid #C4C4C4',
  boxShadow: 24,
  p: 4
}

function NewProductPreparation({ returnToTable }: INews) {
  const {
    GET_ALL_PRODUCTS,
    ALL_PRODUCTS_LIST,
    GET_PRODUCTS,
    PREPARATION_DATA,
    CATEGORY_LIST,
    SELECTED_BREAKDOW_LIST
  } = usePreparation()
  const [inLoading, setLoading] = useState(false)
  const { preparationId } = PREPARATION_DATA
  const schema = newProductDataSchema()

  const {
    values,
    handleSubmit,
    handleChange,
    errors,
    touched,
    setFieldValue,
    setValues
  } = useFormik({
    initialValues: {
      avaria: '',
      avariaId: '',
      categoria: '',
      produto: '',
      valorOrcado: 0
    },
    enableReinitialize: true,
    validationSchema: schema,
    onSubmit: (values) => {
      onSubmit(values)
    }
  })

  const handleBreakdown = (name: string, value: any) => {
    if (!value) {
      setValues({ ...values, [name]: '', avariaId: '' })
      return
    }

    setValues({ ...values, [name]: value.nome, avariaId: value.avariaId })
  }

  const handleCategoryService = (name: string, value: any) => {
    if (!value) {
      setValues({
        ...values,
        [name]: '',
        produto: ''
      })
      return
    }

    setValues({
      ...values,
      [name]: value.nome,
      produto: ''
    })
  }

  const handleNameProduct = (name: string, value: any) => {
    if (!value) return setFieldValue(name, '')

    const findCategory = CATEGORY_LIST?.rows?.find(
      (item: any) => item.id === value.categoriaId
    )

    setValues({
      ...values,
      [name]: value.nome,
      categoria: findCategory?.nome
    })
  }

  async function onSubmit(e: any) {
    setLoading(true)
    const payload = {
      preparacaoId: preparationId,
      produtoId: getIdByName(e.produto, ALL_PRODUCTS_LIST?.rows),
      avariaId: e.avariaId,
      valorOrcado: Number(e.valorOrcado)
    }

    try {
      await preparationService.postProducts(payload)
      await GET_PRODUCTS({
        preparacaoId: preparationId,
        ativo: true
      })
      toast.success('Produto adicionado com sucesso!')
    } catch (e: any) {
      toast.error(e.message)
    } finally {
      setLoading(false)
      returnToTable()
    }
  }

  useEffect(() => {
    GET_ALL_PRODUCTS({
      categoriaId: getParamByParam(
        values.categoria,
        CATEGORY_LIST?.rows,
        'nome',
        'id'
      ),
      ativo: true,
      pageSize: CONSTANTS.allRegisters
    })
  }, [values.categoria])

  return (
    <Modal keepMounted open={true}>
      <CustomView
        backColor="white"
        sx={styleModal}
        style={{ minWidth: '800px' }}
      >
        <CustomView
          backColor="white"
          justifyContent="center"
          p="32px"
          alignItems="center"
        >
          <CustomView borderBottom="1px solid #8F8E914D">
            <CustomGraphy fontSize="18px" mb="16px">
              Adicionar Produto
            </CustomGraphy>
          </CustomView>
          {inLoading ? (
            <Loader inLoading={inLoading} type={'component'} />
          ) : (
            <form onSubmit={handleSubmit} style={{ width: '100%' }}>
              <CustomView
                maxWidth={967}
                mt="36px"
                justifyContent="flex-start"
                gap={1}
                flexDirection={{ xs: 'column', md: 'row' }}
              >
                <FormSelect
                  withBorder={true}
                  label="Avaria"
                  id="avaria"
                  name="avaria"
                  value={values.avaria}
                  onChange={handleBreakdown}
                  options={SELECTED_BREAKDOW_LIST}
                  optionLabel="nome"
                  errorMessage={touched.avaria ? errors?.avaria : ''}
                />
                <FormSelect
                  withBorder={true}
                  label="Categoria do produto"
                  id="categoria"
                  name="categoria"
                  value={values.categoria}
                  onChange={handleCategoryService}
                  options={CATEGORY_LIST?.rows}
                  optionLabel="nome"
                  errorMessage={touched.categoria ? errors?.categoria : ''}
                />
                <FormSelect
                  withBorder={true}
                  label="Nome do Produto"
                  id="produto"
                  name="produto"
                  value={values.produto}
                  onChange={handleNameProduct}
                  options={ALL_PRODUCTS_LIST?.rows}
                  optionLabel="nome"
                  errorMessage={touched.produto ? errors?.produto : ''}
                />
              </CustomView>
              <CustomView
                maxWidth={967}
                mt={{ xs: '2px', md: '15px' }}
                mb="40px"
                gap={1}
                justifyContent="flex-start"
                flexDirection={{ xs: 'column', md: 'row' }}
              >
                <CustomView>
                  <InputCurrency
                    placeholder="R$"
                    id="valorOrcado"
                    label="Valor orçado*"
                    name="valorOrcado"
                    value={values.valorOrcado}
                    variant="outlined"
                    onChange={handleChange}
                    error={
                      touched.valorOrcado && errors?.valorOrcado ? true : false
                    }
                  />
                  {touched.valorOrcado && (
                    <div style={{ color: 'red', fontSize: '12px' }}>
                      {errors?.valorOrcado}
                    </div>
                  )}
                  <div></div>
                </CustomView>
              </CustomView>
              <CustomView
                flexDirection="row"
                alignItems="center"
                justifyContent="space-between"
              >
                <CustomGraphy width="100%" as="lowOpacity">
                  * Campo de preenchimento obrigatório
                </CustomGraphy>
                <CustomView
                  flexDirection="row"
                  alignItems="center"
                  justifyContent="flex-end"
                  gap={8}
                >
                  <CustomButton variable="default" onClick={returnToTable}>
                    Cancelar
                  </CustomButton>
                  <CustomButton variable="bordered" type="submit">
                    Adicionar Produto
                  </CustomButton>
                </CustomView>
              </CustomView>
            </form>
          )}
        </CustomView>
      </CustomView>
    </Modal>
  )
}

export default NewProductPreparation
